import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import MeetingRoom from './pages/MeetingRoom';
import MeetingDetails from './pages/MeetingDetails';
import Recordings from './pages/Recordings';
import GuestJoin from './pages/GuestJoin';
import GuestMeetingRoom from './pages/GuestMeetingRoom';
import InstallPrompt from './components/InstallPrompt';
import UpdatePrompt from './components/UpdatePrompt';
import ErrorBoundary from './components/ErrorBoundary';
import { AppSettingsProvider } from './context/AppSettingsContext';
import GuestLanding from './pages/GuestLanding';
import MeetingEnded from './pages/MeetingEnded';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Pricing from './pages/Pricing';
import AdminPanel from './pages/AdminPanel';
import PaymentSuccess from './pages/PaymentSuccess';
import ConsultationBooking from './pages/ConsultationBooking';
import BlockchainCertificate from './pages/BlockchainCertificate';
import VerifyPage from './pages/VerifyPage';
import Account from './pages/Account';
import './App.css';

// Hide splash screen when app is ready
const hideSplashScreen = () => {
  const splash = document.getElementById('splash-screen');
  if (splash) {
    splash.classList.add('fade-out');
    setTimeout(() => {
      splash.style.display = 'none';
    }, 500);
  }
};

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  return user ? children : <Navigate to="/" />;
}

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/invite/:token" element={<GuestJoin />} />
        <Route path="/guest-meeting/:token" element={<GuestMeetingRoom />} />
        <Route path="/guest-left" element={<GuestLanding />} />
        <Route path="/meeting-ended" element={<MeetingEnded />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/meeting/:id"
          element={
            <PrivateRoute>
              <MeetingRoom />
            </PrivateRoute>
          }
        />
        <Route
          path="/meeting-details/:id"
          element={
            <PrivateRoute>
              <MeetingDetails />
            </PrivateRoute>
          }
        />
        <Route
          path="/recordings"
          element={
            <PrivateRoute>
              <Recordings />
            </PrivateRoute>
          }
        />
        <Route
          path="/pricing"
          element={
            <PrivateRoute>
              <Pricing />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <PrivateRoute>
              <AdminPanel />
            </PrivateRoute>
          }
        />
        <Route
          path="/payment/success"
          element={
            <PrivateRoute>
              <PaymentSuccess />
            </PrivateRoute>
          }
        />
        <Route path="/consultation" element={<ConsultationBooking />} />
        <Route path="/consultation/success" element={<ConsultationBooking />} />
        <Route
          path="/recording/:recordingId/certificate"
          element={
            <PrivateRoute>
              <BlockchainCertificate />
            </PrivateRoute>
          }
        />
        <Route path="/verify/:hash" element={<VerifyPage />} />
        <Route path="/verify" element={<VerifyPage />} />
        <Route
          path="/account"
          element={
            <PrivateRoute>
              <Account />
            </PrivateRoute>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  useEffect(() => {
    hideSplashScreen();
  }, []);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
    <ErrorBoundary>
    <AppSettingsProvider>
    <AuthProvider>
      <BrowserRouter>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />
        <AnimatedRoutes />
        <InstallPrompt />
        <UpdatePrompt />
      </BrowserRouter>
    </AuthProvider>
     </AppSettingsProvider>
    </ErrorBoundary>
    </GoogleOAuthProvider>
  );
}

export default App;
